/**
 * Mapbox popup body for a rover marker — shows the rover's last GGA snapshot
 * plus session metadata from /connections/active.
 */

import { useEffect, useState } from "react";

import type { ActiveConnection, RoverPosition } from "@/types/api";
import { fixQualityMeta } from "@/lib/fixQuality";
import { formatBytes, formatDateTime } from "@/lib/utils";

/** Seconds since `epochSeconds`, re-evaluated every second while mounted. */
function useAgeSeconds(epochSeconds: number): number {
  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => window.clearInterval(t);
  }, []);
  return Math.max(0, Math.round(now - epochSeconds));
}

interface Props {
  rover: RoverPosition;
  connection?: ActiveConnection;
}

function formatCoord(d: number, isLat: boolean): string {
  const hemi = isLat ? (d >= 0 ? "N" : "S") : (d >= 0 ? "E" : "W");
  return `${Math.abs(d).toFixed(6)}° ${hemi}`;
}

export function MapRoverPopup({ rover, connection }: Props) {
  const meta = fixQualityMeta(rover.quality);
  const stale = rover.stale === true;
  const ageS = useAgeSeconds(rover.updated_at);
  return (
    <div className="map-popup" data-testid="map-rover-popup" data-stale={stale ? "true" : "false"}>
      <div className="map-popup__head">
        <div className="map-popup__title" data-testid="map-popup-title">{rover.client_username}</div>
        {stale ? (
          <span className="badge badge--off" title="rtk-service no recibe GGA de este rover">
            Sin datos
          </span>
        ) : (
          <span
            className={`badge ${meta.text}`}
            style={{ background: `${meta.hex}20`, borderColor: meta.hex }}
          >
            {meta.label}
          </span>
        )}
      </div>
      <div className="map-popup__sub">
        en <span className="mono">{rover.mountpoint}</span>
        {" · "}
        <span className={stale ? "mono" : "mono muted"} data-testid="map-rover-age">
          último GGA hace {ageS} s
        </span>
      </div>
      {stale && (
        <div className="map-popup__sub" style={{ color: "#b91c1c" }}>
          Posición, fix y edad de corrección mostrados son los últimos recibidos; el rover
          puede haber perdido cobertura.
        </div>
      )}
      <dl className="map-popup__grid">
        <dt>Lat</dt>
        <dd className="mono">{formatCoord(rover.lat, true)}</dd>
        <dt>Lon</dt>
        <dd className="mono">{formatCoord(rover.lon, false)}</dd>
        <dt>Alt</dt>
        <dd className="mono">
          {rover.alt !== null ? `${rover.alt.toFixed(2)} m` : "—"}
        </dd>
        <dt>Satélites</dt>
        <dd className="mono">{rover.num_sv}</dd>
        <dt>HDOP</dt>
        <dd className="mono">{rover.hdop !== null ? rover.hdop!.toFixed(2) : "—"}</dd>
        <dt>Edad correcc.</dt>
        <dd className="mono">
          {rover.diff_age !== null ? `${rover.diff_age!.toFixed(1)} s` : "—"}
        </dd>
        {connection && (
          <>
            <dt>Desde</dt>
            <dd className="mono">{formatDateTime(connection.connected_since)}</dd>
            <dt>Bytes ↓</dt>
            <dd className="mono">{formatBytes(connection.bytes_received)}</dd>
            <dt>IP</dt>
            <dd className="mono">{connection.remote_addr || "—"}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
